import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatXOF } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CheckCircle2, Video, Headphones, MessageSquare, Copy, Check, Smartphone } from "lucide-react";

const DAY_MAP = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];

export default function Reservation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [a, setA] = useState(null);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [duration, setDuration] = useState(30);
  const [mode, setMode] = useState("video");
  const [anonymous, setAnonymous] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [reference, setReference] = useState("");
  const [copiedField, setCopiedField] = useState("");

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/accompagnateurs/${id}`);
      setA(data.accompagnateur);
    })();
  }, [id]);

  const availableHours = a?.available_hours || [];
  const availableDays = a?.available_days || [];

  const isDayDisabled = (d) => {
    if (!d) return true;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const day = DAY_MAP[d.getDay()];
    return availableDays.length > 0 && !availableDays.includes(day);
  };

  const price = useMemo(() => {
    if (!a) return 0;
    return a[`rate_${duration}`] || 0;
  }, [a, duration]);

  const submit = async () => {
    if (!date || !time) { toast.error("Choisissez une date et une heure."); return; }
    setSubmitting(true);
    try {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const { data } = await api.post("/appointments", {
        accompagnateur_id: id,
        date: `${yyyy}-${mm}-${dd}`,
        time,
        duration_min: duration,
        mode,
        note,
        anonymous,
      });
      setConfirmed(data.appointment);
      toast.success("Réservation confirmée 🌿");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Réservation impossible");
    } finally {
      setSubmitting(false);
    }
  };

  if (!a) return <div className="container-jamm py-20 text-ink-soft">Chargement…</div>;

  if (confirmed) {
    const waveNumber = settings.wave_number || "+221 71 011 72 46";
    const waveDigits = waveNumber.replace(/[^0-9]/g, "");
    const refCode = confirmed.id.slice(-6).toUpperCase();
    const copy = (text, field) => {
      navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success("Copié");
      setTimeout(() => setCopiedField(""), 2000);
    };
    const declarePayment = async () => {
      setPaying(true);
      try {
        await api.post(`/appointments/${confirmed.id}/pay`, {
          method: "wave",
          reference: reference || refCode,
          note: `Paiement Wave vers ${waveNumber}`,
        });
        setPaid(true);
        toast.success("Paiement déclaré. Cathy validera dès réception.");
      } catch (e) {
        toast.error("Impossible de déclarer le paiement.");
      } finally {
        setPaying(false);
      }
    };

    return (
      <div className="container-jamm py-12">
        <div className="max-w-2xl mx-auto" data-testid="reservation-confirmed">
          <div className="card-soft text-center">
            <div className="grid place-items-center w-14 h-14 rounded-full bg-sage-50 text-sage-600 mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h2 className="font-display text-3xl mt-4 text-ink">Votre rendez-vous est réservé</h2>
            <p className="text-ink-soft mt-2">Avec <strong>{confirmed.accompagnateur_name}</strong> · {confirmed.date} à {confirmed.time} · {confirmed.duration_min} min</p>
            <div className="mt-3 text-sm text-ink-mute">Statut : <span className="text-sage-600 font-medium">{confirmed.status}</span></div>
          </div>

          {paid ? (
            <div className="card-soft mt-6 text-center" data-testid="payment-declared">
              <div className="grid place-items-center w-14 h-14 rounded-full bg-gold-light/40 text-gold-dark mx-auto">
                <Check className="w-7 h-7" />
              </div>
              <h3 className="font-display text-2xl mt-4 text-ink">Merci, paiement déclaré</h3>
              <p className="text-ink-soft mt-2">{confirmed.accompagnateur_name} validera votre paiement dès réception. Vous recevrez une notification dans votre espace.</p>
              <div className="flex gap-3 justify-center mt-6">
                <button onClick={() => navigate("/dashboard")} className="btn-primary" data-testid="go-dashboard">Voir mon espace</button>
              </div>
            </div>
          ) : settings.wave_enabled !== false ? (
            <div className="card-soft mt-6" data-testid="wave-payment-card">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#1DC8FF]/15 text-[#0E8DB7]">
                  <Smartphone className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <div>
                  <div className="overline">Paiement Wave</div>
                  <h3 className="font-display text-2xl text-ink mt-1">Finalisez votre réservation</h3>
                </div>
              </div>
              <p className="text-ink-soft mt-4 text-[15px]">
                Envoyez le montant ci-dessous via l'application <strong>Wave</strong> au numéro suivant. Indiquez la <strong>référence</strong> dans la note du paiement.
              </p>

              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                <div className="rounded-2xl border border-border bg-white p-4">
                  <div className="overline text-ink-mute">Numéro Wave</div>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <span className="font-display text-2xl text-ink tracking-wide" data-testid="wave-number">{waveNumber}</span>
                    <button onClick={() => copy(waveDigits, "num")} className="btn-pill border border-border px-3 py-1.5 text-xs hover:border-sage-300" data-testid="wave-copy-num">
                      {copiedField === "num" ? <Check className="w-3.5 h-3.5 text-sage-600" /> : <Copy className="w-3.5 h-3.5" />} Copier
                    </button>
                  </div>
                  <div className="text-xs text-ink-mute mt-1">Destinataire : {settings.wave_recipient || "Cathy Bal"}</div>
                </div>

                <div className="rounded-2xl border border-border bg-white p-4">
                  <div className="overline text-ink-mute">Montant</div>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <span className="font-display text-2xl text-sage-700" data-testid="wave-amount">{formatXOF(confirmed.price)}</span>
                    <button onClick={() => copy(String(confirmed.price), "amt")} className="btn-pill border border-border px-3 py-1.5 text-xs hover:border-sage-300" data-testid="wave-copy-amt">
                      {copiedField === "amt" ? <Check className="w-3.5 h-3.5 text-sage-600" /> : <Copy className="w-3.5 h-3.5" />} Copier
                    </button>
                  </div>
                  <div className="text-xs text-ink-mute mt-1">Référence : <strong>{refCode}</strong></div>
                </div>
              </div>

              <div className="mt-5">
                <label className="overline">Numéro de transaction Wave (optionnel)</label>
                <input
                  value={reference}
                  onChange={(e) => setReference(e.target.value.toUpperCase())}
                  placeholder={refCode}
                  className="mt-2 w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300"
                  data-testid="wave-reference"
                />
                <p className="text-xs text-ink-mute mt-1">Récupérez-le après votre envoi dans l'historique Wave.</p>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                <button onClick={declarePayment} disabled={paying} className="btn-primary" data-testid="declare-payment">
                  {paying ? "Envoi…" : "J'ai effectué le paiement"}
                </button>
                <button onClick={() => navigate("/dashboard")} className="btn-outline" data-testid="pay-later">
                  Payer plus tard
                </button>
              </div>

              <div className="mt-6 rounded-xl bg-beige/60 border border-border p-3 text-xs text-ink-mute leading-relaxed">
                Pour l'instant, seul <strong>Wave</strong> est accepté. Orange Money, Stripe et PayPal arriveront bientôt.
              </div>
            </div>
          ) : (
            <div className="card-soft mt-6 text-center">
              <p className="text-ink-soft">Cathy vous contactera pour finaliser le paiement.</p>
              <button onClick={() => navigate("/dashboard")} className="btn-primary mt-4" data-testid="go-dashboard-2">Voir mon espace</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container-jamm py-12 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8">
        <div className="card-soft">
          <div className="flex items-center gap-4">
            <img src={a.avatar_url} alt={a.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-sage-100" />
            <div>
              <h1 className="font-display text-2xl text-ink">Réserver avec {a.name}</h1>
              <div className="text-ink-soft text-sm">{a.title}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-8">
            <div>
              <div className="overline">1. Choisir une date</div>
              <div className="mt-3 rounded-2xl border border-border bg-white p-2">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={isDayDisabled}
                  className="rounded-md"
                  data-testid="reservation-calendar"
                />
              </div>
              <div className="mt-2 text-xs text-ink-mute">Jours disponibles : {availableDays.join(", ").toUpperCase()}</div>
            </div>

            <div>
              <div className="overline">2. Choisir une heure</div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {availableHours.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setTime(h)}
                    className={`rounded-xl py-2.5 text-sm font-medium border-2 transition-all ${
                      time === h
                        ? "bg-sage-500 text-white border-sage-500 shadow-[0_4px_14px_rgba(122,139,118,0.35)] scale-[1.03]"
                        : "bg-white text-ink-soft border-border hover:border-sage-300 hover:text-ink"
                    }`}
                    data-testid={`time-${h}`}
                    aria-pressed={time === h}
                  >
                    {h}
                  </button>
                ))}
              </div>

              <div className="overline mt-6">3. Durée <span className="text-ink-mute normal-case tracking-normal">· choix actuel : <strong className="text-sage-700">{duration} min</strong></span></div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[15, 30, 60].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDuration(d)}
                    className={`relative rounded-xl py-4 text-sm font-medium border-2 transition-all ${
                      duration === d
                        ? "bg-sage-600 text-white border-sage-600 shadow-[0_6px_18px_rgba(122,139,118,0.35)] scale-[1.04]"
                        : "bg-white text-ink-soft border-border hover:border-sage-300 hover:text-ink"
                    }`}
                    data-testid={`duration-${d}`}
                    aria-pressed={duration === d}
                  >
                    <span className="block font-display text-xl leading-none">{d}</span>
                    <span className={`block text-[11px] mt-1 ${duration === d ? "text-white/80" : "text-ink-mute"}`}>minutes</span>
                    {duration === d && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-gold" />
                    )}
                  </button>
                ))}
              </div>

              <div className="overline mt-6">4. Mode</div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[
                  { v: "video", label: "Vidéo", icon: Video },
                  { v: "audio", label: "Audio", icon: Headphones },
                  { v: "chat", label: "Chat", icon: MessageSquare },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setMode(o.v)}
                    className={`rounded-xl py-3 text-sm font-medium border-2 transition-all flex items-center justify-center gap-1.5 ${
                      mode === o.v
                        ? "bg-gold border-gold text-ink shadow-[0_4px_14px_rgba(213,184,121,0.45)] scale-[1.03]"
                        : "bg-white text-ink-soft border-border hover:border-sage-300 hover:text-ink"
                    }`}
                    data-testid={`mode-${o.v}`}
                    aria-pressed={mode === o.v}
                  >
                    <o.icon className="w-4 h-4" /> {o.label}
                  </button>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between rounded-xl bg-beige/60 border border-border p-3">
                <div>
                  <div className="text-sm font-medium text-ink">Mode anonyme</div>
                  <div className="text-xs text-ink-mute">Votre nom restera caché à l'accompagnateur.</div>
                </div>
                <button type="button" onClick={() => setAnonymous((v) => !v)} className={`w-11 h-6 rounded-full transition ${anonymous ? "bg-sage-500" : "bg-border"}`} data-testid="toggle-anonymous">
                  <span className={`block w-5 h-5 rounded-full bg-white transition shadow translate-y-0.5 ${anonymous ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>

              <div className="mt-4">
                <label className="overline">Note (optionnel)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Quelques mots à partager avec votre accompagnateur…" className="mt-2 w-full rounded-xl border border-border bg-white px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid="reservation-note" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-4">
        <div className="card-soft sticky top-24">
          <h3 className="font-display text-2xl text-ink">Récapitulatif</h3>
          <div className="mt-4 space-y-2 text-[15px] text-ink-soft">
            <div className="flex justify-between"><span>Accompagnateur</span><span className="text-ink font-medium">{a.name}</span></div>
            <div className="flex justify-between"><span>Date</span><span className="text-ink">{date ? date.toLocaleDateString("fr-FR") : "—"}</span></div>
            <div className="flex justify-between"><span>Heure</span><span className="text-ink">{time || "—"}</span></div>
            <div className="flex justify-between"><span>Durée</span><span className="text-ink">{duration} min</span></div>
            <div className="flex justify-between"><span>Mode</span><span className="text-ink capitalize">{mode}</span></div>
            <div className="flex justify-between"><span>Anonyme</span><span className="text-ink">{anonymous ? "Oui" : "Non"}</span></div>
          </div>
          <div className="border-t border-border mt-4 pt-4 flex justify-between items-baseline">
            <span className="text-sm text-ink-soft">Total</span>
            <span className="font-display text-3xl text-sage-700">{formatXOF(price)}</span>
          </div>
          <button onClick={submit} disabled={submitting} className="btn-primary w-full justify-center mt-5" data-testid="reservation-submit">
            {submitting ? "Réservation…" : "Confirmer la réservation"}
          </button>
          <div className="text-[11px] text-ink-mute mt-3 leading-relaxed">
            Le paiement sera disponible prochainement (Wave, Orange Money, Stripe). Pour le moment, votre réservation est marquée <em>en attente</em> ; nous vous contactons pour finaliser.
          </div>
        </div>
      </div>
    </div>
  );
}
